<template>
  <div class="grid w-full grid-cols-12 gap-4">
    <!-- Show "No Results" only when not loading and no products -->
    <slot name="no-results">
      <SFProductListNoResults
        v-if="!loading && !products.length"
        :category="currentCategory?.parent"
        class="col-span-12 max-md:w-fit"
      />
    </slot>

    <!-- Always show existing products (if any) -->
    <template v-if="products.length > 0">
      <SFProductCard
        v-for="(product, index) in products"
        :key="`product-${product.id}`"
        class="col-span-6 mb-5 w-full lg:col-span-4 xl:col-span-3"
        data-testid="product-item"
        :index="index"
        :product="product"
        multiple-images
        :show-siblings="false"
        :listing-meta-data="categoryListingMetaData"
        @intersect:product="onProductIntersect(index)"
        @click-product="(link) => emit('clickProduct', product, index, link)"
      />
    </template>

    <!-- Show loading skeletons when loading (either initial or load more) -->
    <template v-if="loading">
      <SFProductCardSkeleton
        v-for="index in PRODUCT_CARD_SKELETON_LOADERS_SIZE"
        :key="`product-loading-${index}`"
        type="custom"
      />
    </template>

    <!-- Pagination (always show if products exist and pagination is available) -->
    <SFPagination
      v-if="isPaginationVisible && pagination && products.length > 0"
      :pagination="pagination"
      class="col-span-12 mt-6"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import type {
  Category,
  FetchProductsByCategoryResponse,
  Product,
} from '@scayle/storefront-nuxt'
import SFProductCardSkeleton from '../product/card/SFProductCardSkeleton.vue'
import SFProductCard from '../product/card/SFProductCard.vue'
import SFProductListNoResults from './SFProductListNoResults.vue'
import {
  type CollectedRowIntersection,
  usePageState,
  useTrackingEvents,
} from '~/composables'
import { useRowIntersection } from '~/composables'
import {
  categoryListingMetaData,
  PRODUCT_CARD_SKELETON_LOADERS_SIZE,
} from '~/constants'
import { SFPagination } from '#storefront-ui/components'
import { useRoute } from '#app/composables/router'

const {
  loading = true,
  isPaginationVisible = true,
  currentCategory,
  pagination,
  products,
} = defineProps<{
  products: Product[]
  pagination?: FetchProductsByCategoryResponse['pagination']
  loading?: boolean
  isPaginationVisible?: boolean
  currentCategory?: Category | null
}>()

const route = useRoute()
const { collectRowIntersection } = useRowIntersection(() => products)
const { pageState, removeSearchResult } = usePageState()
const { trackSearch } = useTrackingEvents()

const hasTrackedRowIntersection = ref(false)
const isNavigationRestoring = ref(true)

// Give browser time to restore scroll position after navigation
onMounted(() => {
  setTimeout(() => {
    isNavigationRestoring.value = false
  }, 150) // Allow scroll restoration to complete
})

const onProductIntersect = (index: number) => {
  // Skip intersection tracking during scroll restoration to prevent interference
  if (isNavigationRestoring.value) {
    return
  }

  const collectedRowItems = collectRowIntersection(index)

  if (collectedRowItems && !hasTrackedRowIntersection.value) {
    hasTrackedRowIntersection.value = true
    emit('intersect:row', collectedRowItems)
  }
}

watch(
  [() => route.fullPath, () => products, () => currentCategory, () => loading],
  () => {
    if (
      !loading &&
      Object.keys(pageState.value.searchResults).includes(route.path)
    ) {
      const { pagePayload } = JSON.parse(
        pageState.value.searchResults[route.path],
      )
      trackSearch({
        ...JSON.parse(pageState.value.searchResults[route.path]),
        pagePayload: {
          ...pagePayload,
          search_results: pagination?.total || 0,
          null_search: pagination?.total === 0,
        },
      })
      removeSearchResult(route.path)
    }
  },
  { immediate: true },
)

const emit = defineEmits<{
  clickProduct: [product: Product, index: number, link: string | undefined]
  'intersect:row': [collectedRow: CollectedRowIntersection]
}>()
</script>
